<template>
	<div class="chart-base" :style="{ '--chart-height': height }">
		<div
			ref="chartRef"
			class="chart-box"
			:class="{ 'chart-box-empty': isEmpty }"
			:style="{ height: height, width: width }"
		></div>

		<gc-empty v-if="showEmpty && isEmpty" :type="emptyType" :text="emptyText" />

		<div v-if="loading" class="chart-loading">
			<i class="el-icon-loading"></i>
			<span>{{ loadingText }}</span>
		</div>
	</div>
</template>

<script>
// 按需引入 ECharts 模块
const echarts = require('echarts/lib/echarts')
require('echarts/lib/chart/bar')
require('echarts/lib/chart/line')
require('echarts/lib/chart/pie')
require('echarts/lib/chart/scatter')
require('echarts/lib/chart/pictorialBar')
require('echarts/lib/component/title')
require('echarts/lib/component/legend')
require('echarts/lib/component/grid')
require('echarts/lib/component/dataZoom')

import { chartOptionsMerge } from './chartMixin.js'

export default {
	name: 'ChartBase',

	props: {
		// 图表配置选项
		chartOptions: {
			type: Object,
			default: () => ({}),
		},
		// 图表高度
		height: {
			type: String,
			default: '400px',
		},
		// 图表宽度
		width: {
			type: String,
			default: '100%',
		},
		// 是否显示空状态
		showEmpty: {
			type: Boolean,
			default: true,
		},
		// 空状态类型
		emptyType: {
			type: String,
			default: 'chart',
		},
		// 空状态文本
		emptyText: {
			type: String,
			default: '暂无数据',
		},
		// 加载状态
		loading: {
			type: Boolean,
			default: false,
		},
		// 加载文本
		loadingText: {
			type: String,
			default: '图表加载中...',
		},
	},

	data() {
		return {
			chartInstance: null,
		}
	},

	watch: {
		chartOptions: {
			handler(newOptions) {
				if (this.chartInstance && newOptions) {
					this.updateChart(newOptions)
				}
			},
			deep: true,
		},
	},

	mounted() {
		this.initChart()

		// 为图表容器添加标识，便于 PDF 导出时识别
		if (this.$refs.chartRef) {
			this.$refs.chartRef.setAttribute('data-echarts-instance', 'true')
		}
	},

	beforeDestroy() {
		if (this.chartInstance) {
			this.chartInstance.dispose()
			this.chartInstance = null
		}
	},

	methods: {
		initChart() {
			if (!this.$refs.chartRef) return
			requestAnimationFrame(() => {
				try {
					this.chartInstance = echarts.init(this.$refs.chartRef, null, {
						devicePixelRatio: 2,
					})
					const defaultOptions = this.getDefaultOptions()
					const mergedOptions = this.mergeOptions(defaultOptions, this.chartOptions)
					this.chartInstance.setOption(mergedOptions, true)
				} catch (error) {
					console.error('图表初始化失败:', error)
				}
			})
		},

		updateChart(options) {
			if (!this.chartInstance) return

			try {
				const defaultOptions = this.getDefaultOptions()
				const mergedOptions = this.mergeOptions(defaultOptions, options)
				this.chartInstance.setOption(mergedOptions)
			} catch (error) {
				console.error('图表更新失败:', error)
			}
		},

		getDefaultOptions() {
			return {
				title: {
					show: false,
				},
				tooltip: {
					show: false,
				},
				grid: {
					left: 10,
					right: 18,
					bottom: 5,
					top: 10,
					containLabel: true,
				},
				animation: false,
				legend: {
					show: true,
					bottom: 0,
					textStyle: {
						fontSize: 12,
					},
				},
			}
		},

		mergeOptions: chartOptionsMerge,

		// 添加获取图表数据 URL 的方法
		getChartDataURL() {
			if (this.chartInstance) {
				return this.chartInstance.getDataURL({
					type: 'png',
					pixelRatio: 2,
					backgroundColor: '#ffffff',
				})
			}
			return null
		},
	},

	computed: {
		isEmpty() {
			return (
				!this.chartOptions ||
				!this.chartOptions.series ||
				this.chartOptions.series.length === 0 ||
				this.chartOptions.series.every(series => series.data.length === 0)
			)
		},
	},
}
</script>

<style lang="scss" scoped>
.chart-base {
	position: relative;
	width: 100%;

	.chart-box {
		width: 100%;
		min-height: 120px;
	}

	.chart-loading {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		color: #909399;

		.el-icon-loading {
			font-size: 24px;
			margin-bottom: 8px;
		}

		span {
			font-size: 14px;
		}
	}

	::v-deep .gc-blank-page {
		height: var(--chart-height);
		margin-top: 0;
		justify-content: center;

		.no-data-img {
			width: 100px;
			height: auto;
		}

		.text {
			font-size: 11px;
		}
	}

	.chart-box-empty {
		opacity: 0;
		pointer-events: none;
		position: absolute !important;
	}
}
</style>
